import { goTo } from "./goTo";
import { safeExternalUrl, type ExternalUrlRules } from "./safeUrl";

/** How long a press waits on a page that is leaving before it may be pressed again. */
export const LEAVING_MS = 10_000;

/**
 * Sends the browser to `raw` when the rules allow it, and throws when
 * they do not. The promise it returns settles only after `LEAVING_MS`,
 * so the press that started it stays pending, and its button disabled,
 * while the page is leaving: a second press cannot open a second Stripe
 * session.
 */
export function leaveFor(raw: string, hosts?: ExternalUrlRules["hosts"]): Promise<void> {
    const url = safeExternalUrl(raw, { origin: window.location.origin, hosts });
    if (!url) throw new Error("The service gave an address this page does not go to.");
    goTo(url);
    return new Promise((resolve) => window.setTimeout(resolve, LEAVING_MS));
}

/**
 * Points a tab opened on `about:blank` at `raw` when it is a web
 * address, and throws when it is not: that tab has this page's origin,
 * so a script address would run as the page.
 */
export function pointTab(tab: Window | null, raw: string): void {
    const url = safeExternalUrl(raw, { origin: window.location.origin });
    if (!url) throw new Error("The service gave an address this page does not open.");
    if (tab) tab.location.href = url;
    else window.open(url, "_blank", "noopener");
}
